import {Button, Text} from '@/components-v2/atoms'
import {Tab, TabsNavigation} from '@/components-v2/molecules'
import {Modal} from '@/components-v2/organisms'
import {ProductCombination} from '@/containers/Promotions/components/ProductCombination'
import {ExcludeProducts} from '@/containers/Promotions/components/PromotionsConditions/ExcludeProducts'
import {defaultConditon, usePromotion} from '@/context/Promotion/PromotionContext'
import {PromotionConditions} from '@/features/Promotions/components/PromotionConditions'
import PromotionSelectionModalV2 from '@/features/Promotions/components/PromotionSelectionModalV2'
import {PromotionPreview} from '@/features/Promotions/PromotionPreview/PromotionPreview'
import {usePromotionReview} from '@/hooks/usePromotionReview'
import {PromotionType, RewardType} from '@/types/promotion'
import {PromotionContionsTabList} from '@/utils/constants/promotionConstants'
import {createPromotionConditions} from '@/utils/promotions/promotions'
import {useState} from 'react'
import {useSelector} from 'react-redux'
import {useLocation} from 'react-router-dom'

export const PromotionConditionSection = () => {
  const {
    promotionType,
    promotion: finalResult,
    onResultChange,
    setPromoCondition,
    activePromoCondition,
  } = usePromotion()

  const location = useLocation()
  const {isDuplicating} = location.state || {}
  const nonEditable = finalResult?.status?.toUpperCase() === 'PUBLISHED' && !isDuplicating
  const [resetKey, setResetKey] = useState(0)
  const [isPreviewModal, setPreviewModal] = useState(false)

  const conditions = finalResult?.promotionConditions
  const {countryCode} = useSelector(state => state?.countryCode)

  const {createPreviewMessage, isMoreBtnVisible} = usePromotionReview({
    conditions,
    activePromoCondition,
    countryCode,
    promotionType,
  })

  const handleCreateLayer = () => {
    const currentSequence = conditions?.length ? conditions[conditions.length - 1].sequence : 0
    const baseRewards = conditions?.[0]?.rewards ?? []
    const baseConditions = conditions?.[0]?.conditionGroup?.conditions

    const newRewards = baseRewards.map(reward => {
      if (reward?.type === 'FREE_PRODUCT') {
        const rest = {...reward}
        delete rest?.quantity
        return rest
      }
      if (reward?.type === 'FREE_PRODUCT_FROM_LIST') {
        return {...reward}
      }
      return {
        type: reward?.type,
        discountType: reward?.discountType,
      }
    })

    const newConditionGroup =
      promotionType === PromotionType.BUY_MULTIPLE_PRODUCTS
        ? {
            sequence: currentSequence + 1,
            conditionGroup: {
              conditions: baseConditions?.length
                ? baseConditions.map(cond => {
                    const rest = {...cond}
                    delete rest.quantity
                    delete rest.amount
                    return rest
                  })
                : [defaultConditon, defaultConditon],
              ...(conditions?.[0]?.conditionGroup?.combinedWith && {
                combinedWith: conditions[0].conditionGroup.combinedWith,
              }),
            },
            rewards: newRewards,
          }
        : {
            sequence: currentSequence + 1,
            conditionGroup: {
              conditions: conditions?.[0]?.conditionGroup?.conditions?.[0]
                ? conditions[0].conditionGroup.conditions.map(condition => {
                    const rest = {...condition}
                    delete rest.quantity
                    delete rest.amount
                    delete rest.minQuantity
                    return rest
                  })
                : [defaultConditon],
            },
            rewards: newRewards,
          }

    onResultChange({
      promotionConditions: [...(finalResult?.promotionConditions || []), newConditionGroup],
    })
  }

  const handleResetConditions = () => {
    onResultChange({promotionConditions: createPromotionConditions(promotionType)})
    setPromoCondition(0)
    setResetKey(prev => prev + 1)
  }

  const handleTabChange = index => {
    onResultChange({promotionConditions: createPromotionConditions(promotionType)})
    setPromoCondition(index)
  }

  const activeTab = conditions?.[0]?.conditionGroup?.conditions?.[0]?.items?.[0]?.uom
  const activeUOM = PromotionContionsTabList.findIndex(item => item.uom === activeTab)

  const showTabs = promotionType !== PromotionType.STORE_DISCOUNT && promotionType !== PromotionType.WELCOME_DISCOUNT

  const hasFreeProductFromListReward = conditions?.[0]?.rewards?.[0]?.type === RewardType.FREE_PRODUCT_FROM_LIST
  const showAddLayer =
    !hasFreeProductFromListReward &&
    [PromotionType.BUY_SINGLE_PRODUCT, PromotionType.BUY_MULTIPLE_PRODUCTS].includes(promotionType)

  return (
    <>
      <div
        className={`flex flex-col gap-6 rounded-lg bg-[var(--color-background-layer-01)] p-6 ${
          nonEditable ? 'pointer-events-none opacity-50' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <Text headingLevel={5} variant="bold">
            Promotion Conditions*
          </Text>
          <Button variant="secondary" size="small" text="Reset" onClick={handleResetConditions} />
        </div>

        {showTabs && (
          <TabsNavigation
            key={`tab-${resetKey}`}
            selectedIndex={activeUOM > -1 ? activeUOM : activePromoCondition}
            onUpdate={handleTabChange}
            type="line"
          >
            {PromotionContionsTabList.map((tab, i) => (
              <Tab key={tab.id} label={tab.tabLabel} index={i} />
            ))}
          </TabsNavigation>
        )}

        {Array.isArray(conditions) &&
          conditions.map((condition, index) => (
            <PromotionConditions
              key={condition?.sequence ?? index}
              index={index}
              condition={condition}
              promotionType={promotionType}
              selectedPromotionCondition={PromotionContionsTabList[activePromoCondition]?.uom}
            />
          ))}

        {showAddLayer && (
          <Button variant="tertiary" size="medium" text="Add Layer" iconLeading="add_box" onClick={handleCreateLayer} />
        )}

        <ProductCombination />
        <ExcludeProducts key={JSON.stringify(finalResult?.excludedItems)} ModalComponent={PromotionSelectionModalV2} />

        <PromotionPreview
          conditions={conditions}
          activePromoCondition={activePromoCondition}
          countryCode={countryCode}
          promotionType={promotionType}
        />
      </div>

      <Modal
        showModal={isPreviewModal}
        title="Preview"
        type="S"
        hideCloseButton={true}
        showTertiaryButton={true}
        tertiaryButtonProps={{text: 'Cancel', onClick: () => setPreviewModal(false)}}
        text={createPreviewMessage(true)}
        onCloseModal={() => setPreviewModal(false)}
      />
    </>
  )
}
